import { generatePath } from 'react-router-dom'
import { TableColumn } from '../../components/BaseComponents/SimpleSortTable/SimpleSortTable'
import { centsToCurrency, formatEmissionCO2Kg } from '../../lib/utils'
import Button from '../../components/BaseComponents/Button/Button'
import routes from '../../components/Router/routes'
import { PurchaseCarbonData } from '../../types/PurchaseCarbonData'
import { SupplierCarbonData } from '../../types/SupplierCarbonData'
import { Tooltip as ReactTooltip } from 'react-tooltip'

const renderEstimationTypeIcon = (scope: number, estimationType: string, rowId: string) => {
	if (scope !== 3) return null

	if (estimationType === 'supply_chain_active') {
		return (
			<>
				<span
					id={'my' + rowId}
					data-tooltip-content='Your supplier has provided up-to-date figures'
					className='fa-regular fa-circle-check text-algae-500'
				></span>
				<ReactTooltip variant='light' anchorId={'my' + rowId} />
			</>
		)
	}

	if (estimationType === 'supply_chain_nearest') {
		return (
			<>
				<span
					id={'my' + rowId}
					data-tooltip-content='We have used the nearest available data from your supplier, these will change when your supplier updates their accounts'
					className='fa-solid fa-circle-exclamation text-sun-600'
				></span>
				<ReactTooltip variant='light' anchorId={'my' + rowId} />
			</>
		)
	}

	if (estimationType === 'supply_chain_missing') {
		return (
			<>
				<span
					id={'my' + rowId}
					data-tooltip-content="We have estimated these emissions, because we couldn't find any accurate data from your supplier yet"
					className='inline-flex cursor-default select-none items-center gap-1'
				>
					<span className='fa-solid fa-circle-exclamation text-error-500'></span>
					<span>≈</span>
				</span>
				<ReactTooltip variant='light' anchorId={'my' + rowId} />
			</>
		)
	}

	if (estimationType === 'reference_data') {
		return (
			<>
				<span
					className='inline-flex cursor-default select-none'
					id={'my' + rowId}
					data-tooltip-content='We have estimated these emissions using reference data'
				>
					≈
				</span>
				<ReactTooltip variant='light' anchorId={'my' + rowId} />
			</>
		)
	}

	return null
}

export type PurchaseRow = {
	_id: string
	itemName: string
	totalEmissions: string
	totalSpend: number | undefined
	scope: number
	emission_level1: string | null | undefined
	estimationType: string
	level1: string
}

export const purchaseColumns = (navigate: (path: string) => void, formatCurrency: (value: number) => string): TableColumn[] => [
	{ id: 'itemName', label: 'Item Name', sortable: true },
	{ id: 'level1', label: 'Emission Type', sortable: true },
	{
		id: 'refineData',
		label: '',
		sortable: false,
		renderer: (columnKey, rowData: PurchaseRow) => {
			return (
				<>
					{rowData.emission_level1 === null && rowData.scope === 1 ? (
						<Button
							requiresWrite
							type='Primary'
							onClick={() => navigate(generatePath(routes.companyItemEdit.path, { itemId: rowData._id }))}
							icon='fa-solid fa-plus'
							text='Add Emission Type'
							accessibilityLabel={' ' + rowData.itemName}
							size='sm'
							hasLeadingIcon
							isTableRowButton
						/>
					) : null}
				</>
			)
		},
	},
	{
		id: 'totalSpend',
		label: 'Total Spend',
		textAlign: 'right',
		sortable: true,
		renderer: (columnKey, rowData: PurchaseRow) => {
			return <span className='text-white'>{rowData.totalSpend !== undefined ? formatCurrency(rowData.totalSpend) : '-'}</span>
		},
	},
	{
		id: 'estimationType',
		label: '',
		sortable: false,
		columnClass: 'w-10',
		textAlign: 'center',
		renderer: (columnKey, rowData: PurchaseRow) => {
			return <span className='text-white'>{renderEstimationTypeIcon(rowData.scope, rowData.estimationType, rowData._id)}</span>
		},
	},
	{
		id: 'totalEmissions',
		label: 'CO₂e Emissions',
		textAlign: 'right',
		sortable: true,
		renderer: (columnKey, rowData: PurchaseRow) => {
			return (
				<span className='text-white'>
					{rowData.totalEmissions && <span>{formatEmissionCO2Kg(Number(rowData.totalEmissions))} </span>}
				</span>
			)
		},
	},
]

export const constructPurchaseData = (dataSet: Array<PurchaseCarbonData>): PurchaseRow[] =>
	dataSet.map((item) => ({
		_id: item.itemId,
		itemName: item.itemName,
		totalEmissions: item.totalEmissions,
		totalSpend: centsToCurrency(item.totalSpend),
		scope: Number(item.scope),
		emission_level1: item.level1,
		estimationType: item.estimationType,
		level1:
			Number(item.scope) === 1
				? item.level1 !== undefined && item.level1 !== null
					? item.level1 + ' (Scope 1)'
					: 'Scope 1'
				: Number(item.scope) === 2
					? 'Electricity (Scope 2)'
					: 'Value chain (Scope 3)',
	}))

export type SupplierRow = {
	_id: string
	supplierName: string
	scope: number
	totalEmissions: string
	totalSpend: number | undefined
	supplierIndustryCodeId: string | null
	estimationType: string
	invited: boolean
	invitationId: string | null
	invitationStatus: string | null
	isAccepted: boolean
	jurisdictionCode: string | null
}

export const supplierColumns = (
	navigate: (path: string, options?: { state?: string }) => void,
	formatCurrency: (value: number) => string,
	onResendInvite: (invitationId: string, supplierName: string) => void
): TableColumn[] => [
	{ id: 'supplierName', label: 'Supplier Name', sortable: true },
	{
		id: 'refineData',
		label: '',
		sortable: false,
		renderer: (columnKey, rowData: SupplierRow) => {
			return (
				<>
					{(rowData.supplierIndustryCodeId === null || rowData.jurisdictionCode === null) && rowData.scope === 3 ? (
						<Button
							requiresWrite
							type='Primary'
							onClick={() =>
								navigate(generatePath(routes.companySupplierEdit.path, { supplierId: rowData._id }), { state: 'carbon' })
							}
							icon='fa-solid fa-plus'
							text='Add Supplier Info'
							accessibilityLabel={' ' + rowData.supplierName}
							size='sm'
							hasLeadingIcon
							isTableRowButton
						/>
					) : rowData.supplierIndustryCodeId !== null &&
						rowData.jurisdictionCode !== null &&
						rowData.scope === 3 &&
						rowData.invitationStatus === 'open' &&
						!rowData.isAccepted ? (
						<Button
							requiresWrite
							type='Outlined'
							onClick={() => rowData.invitationId && onResendInvite(rowData.invitationId, rowData.supplierName)}
							icon='fa-solid fa-paper-plane'
							text='Resend Invite'
							accessibilityLabel={' ' + rowData.supplierName}
							size='sm'
							hasLeadingIcon
							isTableRowButton
						/>
					) : rowData.supplierIndustryCodeId !== null &&
						rowData.jurisdictionCode !== null &&
						rowData.scope === 3 &&
						!rowData.invited ? (
						<Button
							requiresWrite
							type='Primary'
							onClick={() => navigate(generatePath(routes.companyInviteSupplier.path, { supplierId: rowData._id }))}
							icon='fa-solid fa-user-plus'
							text='Invite Supplier'
							accessibilityLabel={' ' + rowData.supplierName}
							size='sm'
							hasLeadingIcon
							isTableRowButton
						/>
					) : null}
				</>
			)
		},
	},
	{
		id: 'totalSpend',
		label: 'Total Spend',
		textAlign: 'right',
		sortable: true,
		renderer: (columnKey, rowData: SupplierRow) => {
			return <p className='text-white'>{rowData.totalSpend !== undefined ? formatCurrency(rowData.totalSpend) : '-'}</p>
		},
	},
	{
		id: 'estimationType',
		label: '',
		sortable: false,
		columnClass: 'w-10',
		textAlign: 'center',
		renderer: (columnKey, rowData: SupplierRow) => {
			return <span className='text-white'>{renderEstimationTypeIcon(rowData.scope, rowData.estimationType, rowData._id)}</span>
		},
	},
	{
		id: 'totalEmissions',
		label: 'CO₂e Emissions',
		textAlign: 'right',
		sortable: true,
		renderer: (columnKey, rowData: SupplierRow) => {
			return <div className='text-white'>{rowData.totalEmissions && formatEmissionCO2Kg(Number(rowData.totalEmissions))}</div>
		},
	},
]

export const constructSupplierData = (dataSet: Array<SupplierCarbonData>): SupplierRow[] =>
	dataSet.map((supplier) => ({
		_id: supplier.supplierId,
		supplierName: supplier.supplierName,
		scope: Number(supplier.scope),
		totalEmissions: supplier.totalEmissions,
		totalSpend: centsToCurrency(Number(supplier.totalSpend)),
		supplierIndustryCodeId: supplier.supplierIndustryCodeId,
		estimationType: supplier.estimationType,
		invited: supplier.status === 'open' || supplier.is_accepted,
		invitationId: supplier.invitationId,
		invitationStatus: supplier.status,
		isAccepted: supplier.is_accepted,
		jurisdictionCode: supplier.jurisdictionCode,
	}))
